import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  MapPin, 
  Building2, 
  Clock, 
  CheckCircle2, 
  Circle,
  Play,
  Square,
  Camera,
  Upload,
  Navigation,
  AlertTriangle,
  Shield,
  Loader2,
  X,
  ChevronRight,
  FileText,
  RefreshCw
} from 'lucide-react';

export default function SectorExecutionPage() {
  const { siteId, shiftId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [siteData, setSiteData] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [activeVisit, setActiveVisit] = useState(null);
  const [visitResponses, setVisitResponses] = useState({});
  const [visitAttachments, setVisitAttachments] = useState([]);
  const [gpsStatus, setGpsStatus] = useState('pending');
  const [gpsCoords, setGpsCoords] = useState(null);
  const [checkinLoading, setCheckinLoading] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [responseLoading, setResponseLoading] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [missingDirectives, setMissingDirectives] = useState([]);
  const [sectorStats, setSectorStats] = useState({});
  
  const fileInputRef = useRef(null);

  // Fetch site and sectors data
  const fetchData = useCallback(async () => {
    if (!siteId) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/sector-execution/sites/${siteId}/sectors`);
      setSiteData({
        site_id: response.data.site_id,
        site_name: response.data.site_name,
        site_code: response.data.site_code,
      });
      setSectors(response.data.sectors || []);
      
      // Check for any active visits
      const activeResponse = await api.get(`/sector-execution/visits/active?site_id=${siteId}`);
      if (activeResponse.data.length > 0) {
        const visit = activeResponse.data[0];
        setActiveVisit(visit);
        await fetchVisitDetails(visit.id);
      }
      
      // Get completed sectors from history
      const historyResponse = await api.get(`/sector-execution/visits/history?site_id=${siteId}&limit=100`);
      const completed = new Set(
        historyResponse.data
          .filter(v => v.status === 'completed')
          .map(v => v.sector_id)
      );
      setCompletedSectors(completed);
      
    } catch (error) {
      console.error('Error fetching sector data:', error);
      toast.error('Failed to load sector data');
    } finally {
      setLoading(false);
    }
  }, [api, siteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Capture GPS position
  const captureGPS = () => {
    setGpsStatus('capturing');
    
    if (!navigator.geolocation) {
      setGpsStatus('error');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setGpsCoords({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy_m: position.coords.accuracy,
        });
        setGpsStatus('captured');
      },
      (error) => {
        console.error('GPS error:', error);
        setGpsStatus('error');
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    captureGPS();
    const interval = setInterval(captureGPS, 30000); // Refresh GPS every 30 seconds
    return () => clearInterval(interval);
  }, []);

  // Fetch visit details with responses
  const fetchVisitDetails = async (visitId) => {
    try {
      const response = await api.get(`/sector-execution/visits/${visitId}`);
      const visit = response.data;
      
      // Convert responses to a map
      const responsesMap = {};
      (visit.responses || []).forEach(r => {
        responsesMap[r.sector_directive_id] = r;
      });
      setVisitResponses(responsesMap);
      setVisitAttachments(visit.attachments || []);
    } catch (error) {
      console.error('Error fetching visit details:', error);
    }
  };

  // Check into a sector
  const handleCheckin = async (sector) => {
    setCheckinLoading(sector.id);
    
    try {
      const response = await api.post('/sector-execution/visits/checkin', {
        shift_id: shiftId || null,
        site_id: siteId,
        sector_id: sector.id,
        gps_lat: gpsCoords?.latitude,
        gps_lng: gpsCoords?.longitude,
        accuracy_m: gpsCoords?.accuracy_m,
      });
      
      setActiveVisit(response.data);
      setVisitResponses({});
      setVisitAttachments([]);
      toast.success(`Checked into ${sector.name}`);
    } catch (error) {
      console.error('Error checking in:', error);
      toast.error(error.response?.data?.detail || 'Failed to check in');
    } finally {
      setCheckinLoading(null);
    }
  };

  // Submit directive response
  const handleResponse = async (directive, value) => {
    if (!activeVisit) return;
    
    setResponseLoading(directive.id);
    
    try {
      let payload = {
        sector_directive_id: directive.id,
        response_type: directive.input_type,
        gps_lat: gpsCoords?.latitude,
        gps_lng: gpsCoords?.longitude,
        accuracy_m: gpsCoords?.accuracy_m,
      };
      
      // Set value based on type
      if (directive.input_type === 'checkbox') {
        payload.value_bool = value;
      } else if (directive.input_type === 'yes_no') {
        payload.value_enum = value;
      } else if (directive.input_type === 'text') {
        payload.value_text = value;
      } else if (directive.input_type === 'photo_required') {
        payload.value_bool = true; // Mark as acknowledged
      }
      
      const response = await api.post(
        `/sector-execution/visits/${activeVisit.id}/response`,
        payload
      );
      
      setVisitResponses(prev => ({
        ...prev,
        [directive.id]: response.data
      }));
      
    } catch (error) {
      console.error('Error submitting response:', error);
      toast.error(error.response?.data?.detail || 'Failed to save response');
    } finally {
      setResponseLoading(null);
    }
  };

  // Upload photo
  const handlePhotoUpload = async (e, directiveId = null) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    
    setUploadLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      // Determine entity type and ID
      let entityType = 'sector_visit';
      let entityId = activeVisit.id;
      
      if (directiveId) {
        const response = visitResponses[directiveId];
        if (response) {
          entityType = 'directive_response';
          entityId = response.id;
        }
      }
      
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId);
      
      if (gpsCoords) {
        formData.append('gps_lat', gpsCoords.latitude);
        formData.append('gps_lng', gpsCoords.longitude);
        formData.append('accuracy_m', gpsCoords.accuracy_m);
      }
      
      const response = await api.post('/sector-execution/attachments/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      setVisitAttachments(prev => [...prev, response.data]);
      toast.success('Photo uploaded');
      
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Attempt checkout
  const handleCheckout = async () => {
    if (!activeVisit) return;
    
    setCheckoutLoading(true);
    
    try {
      const response = await api.post(
        `/sector-execution/visits/${activeVisit.id}/checkout`,
        {
          gps_lat: gpsCoords?.latitude,
          gps_lng: gpsCoords?.longitude,
          accuracy_m: gpsCoords?.accuracy_m,
        }
      );
      
      // Success - mark sector as completed
      await fetchData();
      setActiveVisit(null);
      setVisitResponses({});
      setVisitAttachments([]);
      toast.success('Sector completed');
      
    } catch (error) {
      console.error('Checkout error:', error);
      
      // Check if it's a missing directives error
      const detail = error.response?.data?.detail;
      if (detail?.missing_directives) {
        setMissingDirectives(detail.missing_directives);
        setShowOverrideDialog(true);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to checkout');
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Checkout with override
  const handleOverrideCheckout = async () => {
    if (!activeVisit || !overrideReason.trim()) return;
    
    setCheckoutLoading(true);
    
    try {
      await api.post(
        `/sector-execution/visits/${activeVisit.id}/checkout`,
        {
          gps_lat: gpsCoords?.latitude,
          gps_lng: gpsCoords?.longitude,
          accuracy_m: gpsCoords?.accuracy_m,
          override_reason: overrideReason.trim(),
        }
      );
      
      await fetchData();
      setActiveVisit(null);
      setVisitResponses({});
      setVisitAttachments([]);
      setShowOverrideDialog(false);
      setOverrideReason('');
      setMissingDirectives([]);
      toast.success('Sector completed with override');
      
    } catch (error) {
      console.error('Override checkout error:', error);
      toast.error('Failed to checkout');
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Get active sector data
  const activeSector = activeVisit 
    ? sectors.find(s => s.id === activeVisit.sector_id) 
    : null;

  // Calculate progress
  const totalSectors = sectors.length;
  const completedCount = completedSectors.size;
  const progressPercent = totalSectors > 0 ? (completedCount / totalSectors) * 100 : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24" data-testid="sector-execution-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-rajdhani font-bold text-white tracking-wide">
            SECTOR EXECUTION
          </h1>
          <p className="text-zinc-400 text-sm flex items-center gap-2 mt-1">
            <Building2 className="w-4 h-4" />
            {siteData?.site_name} ({siteData?.site_code})
          </p>
        </div>
        
        <div className="flex items-center gap-4">
          {/* GPS Status */}
          <div className="flex items-center gap-2">
            <Navigation className={`w-4 h-4 ${
              gpsStatus === 'captured' ? 'text-emerald-400' :
              gpsStatus === 'capturing' ? 'text-yellow-400 animate-pulse' :
              'text-red-400'
            }`} />
            <span className="text-xs text-zinc-400">
              {gpsStatus === 'captured' ? 'GPS Active' :
               gpsStatus === 'capturing' ? 'Getting GPS...' :
               'GPS Error'}
            </span>
          </div>
          
          <Button
            variant="outline"
            size="sm"
            onClick={fetchData}
            className="border-zinc-600"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Progress Card */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-400">Sectors Completed</span>
            <span className="text-sm font-mono">
              <span className="text-emerald-400">{completedCount}</span>
              <span className="text-zinc-500"> / </span>
              <span className="text-zinc-300">{totalSectors}</span>
            </span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-[#27272a]" />
        </CardContent>
      </Card>

      {/* Active Visit */}
      {activeVisit && activeSector && (
        <Card className="bg-[#18181b] border-emerald-500/50 shadow-[0_0_20px_rgba(16,185,129,0.1)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-sm bg-emerald-500/20 flex items-center justify-center">
                  <Play className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <CardTitle className="text-lg font-rajdhani text-white">
                    {activeSector.name}
                  </CardTitle>
                  <p className="text-xs text-zinc-500 flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    Checked in at {new Date(activeVisit.checkin_at).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false })}
                  </p>
                </div>
              </div>
              <Badge className="bg-emerald-500/20 text-emerald-400">IN PROGRESS</Badge>
            </div>
          </CardHeader>
          
          <CardContent className="pt-4">
            {/* Directives */}
            <div className="space-y-4">
              <h4 className="text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">
                Directives ({activeSector.directives?.length || 0})
              </h4>
              
              <ScrollArea className="max-h-[400px]">
                <div className="space-y-3 pr-4">
                  {(activeSector.directives || []).map((directive) => (
                    <DirectiveInput
                      key={directive.id}
                      directive={directive}
                      response={visitResponses[directive.id]}
                      attachments={visitAttachments.filter(a => 
                        a.entity_type === 'directive_response' && 
                        a.entity_id === visitResponses[directive.id]?.id
                      )}
                      onResponse={(value) => handleResponse(directive, value)}
                      onUpload={(e) => handlePhotoUpload(e, directive.id)}
                      loading={responseLoading === directive.id}
                      uploadLoading={uploadLoading}
                    />
                  ))}
                </div>
              </ScrollArea>
              
              {/* General Photo Upload */}
              <div className="pt-4 border-t border-[#3f3f46]">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-zinc-300">Additional Photos</p>
                    <p className="text-xs text-zinc-500">
                      {visitAttachments.filter(a => a.entity_type === 'sector_visit').length} uploaded
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadLoading}
                    className="border-zinc-600"
                  >
                    {uploadLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Camera className="w-4 h-4 mr-2" />
                    )}
                    Upload Photo
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => handlePhotoUpload(e)}
                    className="hidden"
                  />
                </div>
              </div>
            </div>
            
            {/* Checkout Button */}
            <div className="mt-6 flex gap-2">
              <Button
                onClick={handleCheckout}
                disabled={checkoutLoading}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                data-testid="checkout-sector-btn"
              >
                {checkoutLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Square className="w-4 h-4 mr-2" />
                )}
                Complete Sector
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sector List */}
      {!activeVisit && (
        <div className="space-y-3">
          <h3 className="text-xs font-rajdhani font-semibold text-zinc-400 uppercase tracking-wider">
            Sectors ({sectors.length})
          </h3>
          
          {sectors.map((sector, index) => {
            const isCompleted = completedSectors.has(sector.id);
            
            return (
              <Card 
                key={sector.id}
                className={`bg-[#18181b] border-[#3f3f46] ${
                  hasVisits
                      ? 'border-emerald-500/30'
                      : 'border-red-500/40 hover:border-red-500/60'
                } transition-colors`}
                data-testid={`sector-card-${sector.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isCompleted 
                          ? 'bg-emerald-500/20 text-emerald-400' 
                          : 'bg-zinc-800 text-zinc-400'
                      }`}>
                        {isCompleted ? (
                          <CheckCircle2 className="w-5 h-5" />
                        ) : (
                          <span className="text-sm font-mono">{index + 1}</span>
                        )}
                      </div>
                      <div>
                        <h4 className="text-sm font-medium text-white">{sector.name}</h4>
                        <p className="text-xs text-zinc-500">
                          {sector.directives?.length || 0} directives
                          {sector.gps_lat && ` • GPS marked`}
                        </p>
                      </div>
                    </div>
                    
                    {!isCompleted && (
                      <Button
                        onClick={() => handleCheckin(sector)}
                        disabled={checkinLoading === sector.id}
                        size="sm"
                        className="bg-emerald-500 hover:bg-emerald-600"
                        data-testid={`checkin-sector-${sector.id}`}
                      >
                        {checkinLoading === sector.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Play className="w-4 h-4 mr-1" />
                            Check In
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Override Dialog */}
      <Dialog open={showOverrideDialog} onOpenChange={setShowOverrideDialog}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400" />
              Missing Required Directives
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              The following required items are incomplete:
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <ul className="space-y-2">
              {missingDirectives.map((item, index) => (
                <li key={index} className="flex items-center gap-2 text-sm text-zinc-300">
                  <Circle className="w-2 h-2 text-red-400" />
                  {item}
                </li>
              ))}
            </ul>
            
            <div className="space-y-2">
              <Label className="text-zinc-300">Override Reason (Required)</Label>
              <Textarea
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Explain why these directives could not be completed..."
                className="bg-[#09090b] border-[#3f3f46] min-h-[100px]"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowOverrideDialog(false)}
              className="border-zinc-600"
            >
              Go Back
            </Button>
            <Button 
              onClick={handleOverrideCheckout}
              disabled={!overrideReason.trim() || checkoutLoading}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
            >
              {checkoutLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <AlertTriangle className="w-4 h-4 mr-2" />
              )}
              Complete with Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Directive Input Component
function DirectiveInput({ directive, response, attachments, onResponse, onUpload, loading, uploadLoading }) {
  const [textValue, setTextValue] = useState(response?.value_text || '');
  const fileInputRef = useRef(null);
  
  const isCompleted = !!response;
  
  const handleTextBlur = () => {
    if (textValue !== (response?.value_text || '')) {
      onResponse(textValue);
    }
  };
  
  return (
    <div className={`p-3 rounded-sm border ${
      isCompleted 
        ? 'bg-emerald-500/5 border-emerald-500/30' 
        : 'bg-[#27272a] border-[#3f3f46]'
    }`}>
      <div className="flex items-start gap-3">
        <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
          isCompleted ? 'bg-emerald-500/20' : 'bg-zinc-800'
        }`}>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
          ) : isCompleted ? (
            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
          ) : (
            <Circle className="w-4 h-4 text-zinc-500" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <p className="text-sm text-white">{directive.title}</p>
            {directive.required && (
              <Badge className="text-xs bg-red-500/20 text-red-400">Required</Badge>
            )}
          </div>
          
          {/* Checkbox */}
          {directive.input_type === 'checkbox' && (
            <div className="flex items-center gap-2">
              <Checkbox
                checked={response?.value_bool || false}
                onCheckedChange={(checked) => onResponse(checked)}
                disabled={loading}
                className="border-zinc-600 data-[state=checked]:bg-emerald-500"
              />
              <span className="text-sm text-zinc-400">Confirmed</span>
            </div>
          )}
          
          {/* Yes/No */}
          {directive.input_type === 'yes_no' && (
            <RadioGroup
              value={response?.value_enum || ''}
              onValueChange={(value) => onResponse(value)}
              className="flex gap-4"
              disabled={loading}
            >
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id={`yes-${directive.id}`} className="border-zinc-600" />
                <Label htmlFor={`yes-${directive.id}`} className="text-sm text-zinc-300">Yes</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id={`no-${directive.id}`} className="border-zinc-600" />
                <Label htmlFor={`no-${directive.id}`} className="text-sm text-zinc-300">No</Label>
              </div>
            </RadioGroup>
          )}
          
          {/* Text */}
          {directive.input_type === 'text' && (
            <Textarea
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
              onBlur={handleTextBlur}
              placeholder="Enter notes..."
              className="bg-[#09090b] border-[#3f3f46] text-sm min-h-[60px]"
              disabled={loading}
            />
          )}
          
          {/* Photo Required */}
          {directive.input_type === 'photo_required' && (
            <div className="space-y-2">
              {attachments.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {attachments.map((att, i) => (
                    <img 
                      key={i}
                      src={att.file_url}
                      alt={`Photo ${i + 1}`}
                      className="w-16 h-16 object-cover rounded border border-[#3f3f46]"
                    />
                  ))}
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadLoading}
                className="border-zinc-600"
              >
                {uploadLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Camera className="w-4 h-4 mr-2" />
                )}
                {attachments.length > 0 ? 'Add More' : 'Take Photo'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onUpload}
                className="hidden"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
